'use client'

import styles from './Tooltip.module.css'
import classnames from 'classnames'
import { useState, useEffect, useRef } from 'react'

interface TooltipProps {
  content: string
  children?: React.ReactNode
}

export const Tooltip = ({ content, children }: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const tooltipElementRef = useRef<HTMLSpanElement>(null)
  const tooltipButtonRef = useRef<HTMLButtonElement>(null)
  const tooltipWrapperRef = useRef<HTMLDivElement>(null)
  const gap = 12

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      // Added to prevent the tooltip immediately closing/re-opening when the icon has been interacted with - respects the toggling behaviour
      if (tooltipWrapperRef.current && tooltipWrapperRef.current.contains(event.target as Node)) {
        return
      }

      if (tooltipElementRef.current && !tooltipElementRef.current.contains(event.target as Node)) {
        setIsVisible(false)
      }
    }

    if (isVisible) {
      window.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      window.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isVisible])

  const adjustPosition = () => {
    if (tooltipElementRef.current) {
      const tooltipRect = tooltipElementRef.current.getBoundingClientRect()
      const triggerRect = tooltipButtonRef.current?.getBoundingClientRect()
      const iconHeight = triggerRect?.height
      const iconWidth = triggerRect?.width

      const adjustedGap = gap
      const isCloserToLeft = tooltipRect.left < window.innerWidth / 2
      if (!isCloserToLeft) {
        tooltipElementRef.current.style.left = `-${Math.round(tooltipRect.width - (iconWidth ?? 0))}px`
      }

      // Set initial position to the top above the icon
      tooltipElementRef.current.style.top = `-${Math.round(tooltipRect.height + adjustedGap)}px`

      // Check if the tooltip goes off the top edge of the screen
      if (tooltipRect.top <= adjustedGap) {
        tooltipElementRef.current.style.top = `${(iconHeight ?? 0) + adjustedGap}px`
      }
    }
  }

  const toggleTooltip = () => {
    setIsVisible(!isVisible)
    adjustPosition()
  }

  const tooltipClasses = classnames(styles.Tooltip, {
    [styles[`Tooltip--long-text`]]: content.length >= 30,
    [styles[`Tooltip--text-is-visible`]]: isVisible,
  })

  return (
    <div className={tooltipClasses} ref={tooltipWrapperRef}>
      <button className={styles.Tooltip__icon} ref={tooltipButtonRef} onClick={toggleTooltip} type="button">
        {children}
      </button>

      <span
        className={styles.Tooltip__text}
        dangerouslySetInnerHTML={{ __html: content }}
        ref={tooltipElementRef}
        role="tooltip"
      />
    </div>
  )
}
